import {
    Alert,
    Anchor,
    Badge,
    Box,
    Button,
    Checkbox,
    Group,
    Loader,
    Modal,
    NumberInput,
    Paper,
    Radio,
    Stack,
    Table,
    Text,
    Title,
    Tooltip,
} from '@mantine/core'
import {DateInput, TimeInput} from '@mantine/dates'
import {IconAlertTriangle} from '@tabler/icons-react'
import {Link} from '@tanstack/react-router'
import {differenceInCalendarDays, format} from 'date-fns'
import type {ReactNode} from 'react'
import {useEffect, useMemo, useState} from 'react'

import {businessCalendarDate, formatBusinessTimestamp} from 'src/api/business-time'
import {useEditRights} from 'src/jobs/edit-rights-context'
import {toLocalDate} from 'src/jobs/job-schedule-format'
import {formatPrice} from 'src/lib/format-price'
import type {ProjectJobSummary} from 'src/projects/schemas'
import {RentalBillingModal} from 'src/rental-billing/rental-billing-modal'
import {ConfirmOutButton} from 'src/rentals/confirm-out-button'

import {ActiveYardSelector} from './active-yard-selector'
import {DeployEquipmentDialog} from './deploy-equipment-dialog'
import {GroupActionButton} from './group-action-button'
import type {ProjectDeployedEquipment, ProjectPendingEquipmentRow} from './schemas'
import {stintDatesLabel, stintTimesTitle} from './stint-dates'
import {
    useInvalidateProjectDeployedEquipment,
    useProjectDeployedEquipment,
    useReturnProjectIdentifiedEquipment,
    useReturnProjectStockEquipment,
    useReturnProjectThirdPartyEquipment,
} from './use-project-deployed-equipment'

type ProjectDeployedEquipmentSectionProps = {
    projectId: string
    customerId: string
    jobs: ProjectJobSummary[]
    /** Dispatch of the order's yard, managers and admins confirm automatic rentals went out. */
    canConfirmOut: boolean
}

/** How an asset is tracked — a per-row badge, not a section heading. */
import {stockSourceKey} from 'src/equipment/stock-source'

type TrackingModel = 'stock' | 'identified' | 'third-party'

type OnSiteRow =
    | ({
          kind: 'stock'
          key: string
      } & ProjectDeployedEquipment['stockRows'][number])
    | ({
          kind: 'identified'
          key: string
      } & ProjectDeployedEquipment['identifiedRows'][number])
    | ({
          kind: 'third-party'
          key: string
      } & ProjectDeployedEquipment['thirdPartyRows'][number])

type ReturnedRow =
    | ({
          kind: 'stock'
          key: string
      } & ProjectDeployedEquipment['returnedStockRows'][number])
    | ({
          kind: 'identified'
          key: string
      } & ProjectDeployedEquipment['returnedIdentifiedRows'][number])
    | ({
          kind: 'third-party'
          key: string
      } & ProjectDeployedEquipment['returnedThirdPartyRows'][number])

type ReturnedRowKeySource = {
    rentalStintIds: string[]
    rentalAssetTypeId: string
    locationLabel: string
    deployedAt: Date
    returnedAt: Date
    billingType?: string | null
    billingRate?: string | null
    jobId?: string | null
    rentalAssetId?: string
    thirdPartyRentalAssetOfferingId?: string | null
}

type RentalTermsRow = {
    billingTermsMixed?: boolean
    billingType?: string | null
    billingRate?: string | null
}

const billingUnitAbbreviations: Record<string, string> = {
    hourly: 'H',
    daily: 'D',
    weekly: 'W',
    monthly: 'M',
    flat: 'F',
    per_load: 'load',
}

/** select, equipment, qty, middle columns, terms, billing/actions */
const COLUMN_COUNT = 7

function equipmentName(row: OnSiteRow | ReturnedRow) {
    return row.kind === 'identified' ? row.assetDisplayName : row.rentalAssetTypeName
}

function pendingEquipmentName(row: ProjectPendingEquipmentRow) {
    return row.party === 'first_party' && row.assetDisplayName != null
        ? row.assetDisplayName
        : row.rentalAssetTypeName
}

function returnedRowKey(row: ReturnedRowKeySource) {
    const rentalStintKey = [...row.rentalStintIds].sort().join('-')
    if (rentalStintKey) {
        return `returned-stints-${rentalStintKey}`
    }

    return [
        'returned-history',
        row.rentalAssetId ?? row.rentalAssetTypeId,
        row.thirdPartyRentalAssetOfferingId ?? 'owned',
        row.jobId ?? 'no-job',
        row.billingType ?? 'no-terms',
        row.billingRate ?? 'no-rate',
        row.locationLabel,
        row.deployedAt.toISOString(),
        row.returnedAt.toISOString(),
    ].join('-')
}

function returnedSelectionLabel(name: string, returnedAt: Date) {
    return `Select ${name} returned ${format(returnedAt, 'MMM d, yyyy')}`
}

function isFutureReturnDate(value: Date) {
    return differenceInCalendarDays(value, businessCalendarDate()) > 0
}

function rowQuantity(row: OnSiteRow | ReturnedRow) {
    return row.kind === 'stock' ? row.quantity : 1
}

function pendingTrackingModel(row: ProjectPendingEquipmentRow): TrackingModel {
    if (row.party === 'third_party') {
        return 'third-party'
    }
    return row.isUniquelyIdentified ? 'identified' : 'stock'
}

function formatUnits(count: number) {
    return count === 1 ? '1 unit' : `${count} units`
}

function TrackingBadge({model}: {model: TrackingModel}) {
    if (model === 'stock') {
        return (
            <Badge variant="light" color="gray" size="xs">
                Stock
            </Badge>
        )
    }
    if (model === 'third-party') {
        return (
            <Badge variant="light" color="pink" size="xs" w="80px">
                3rd party
            </Badge>
        )
    }
    return (
        <Badge variant="light" color="blue" size="xs">
            Unit
        </Badge>
    )
}

type EquipmentNameLink =
    | {to: '/rental-assets/stock/$typeId'; params: {typeId: string}}
    | {to: '/rental-assets/$assetId'; params: {assetId: string}}

function equipmentNameLink(row: OnSiteRow | ReturnedRow): EquipmentNameLink | undefined {
    if (row.kind === 'stock') {
        if (row.party === 'third_party') return undefined
        return {
            to: '/rental-assets/stock/$typeId',
            params: {typeId: row.rentalAssetTypeId},
        }
    }
    if (row.kind === 'identified') {
        return {
            to: '/rental-assets/$assetId',
            params: {assetId: row.rentalAssetId},
        }
    }
    return undefined
}

function pendingEquipmentNameLink(row: ProjectPendingEquipmentRow): EquipmentNameLink | undefined {
    if (row.party === 'third_party') {
        return undefined
    }
    if (row.isUniquelyIdentified && row.rentalAssetId != null) {
        return {
            to: '/rental-assets/$assetId',
            params: {assetId: row.rentalAssetId},
        }
    }
    if (!row.isUniquelyIdentified) {
        return {
            to: '/rental-assets/stock/$typeId',
            params: {typeId: row.rentalAssetTypeId},
        }
    }
    return undefined
}

function EquipmentCell({
    name,
    model,
    link,
    thirdPartyRentalCompanyName,
}: {
    name: string
    model: TrackingModel
    link?: EquipmentNameLink
    thirdPartyRentalCompanyName?: string | null
}) {
    const nameElement =
        link?.to === '/rental-assets/stock/$typeId' ? (
            <Anchor
                component={Link}
                to="/rental-assets/stock/$typeId"
                params={{typeId: link.params.typeId}}
                size="sm"
                fw={500}
            >
                {name}
            </Anchor>
        ) : link?.to === '/rental-assets/$assetId' ? (
            <Anchor
                component={Link}
                to="/rental-assets/$assetId"
                params={{assetId: link.params.assetId}}
                size="sm"
                fw={500}
            >
                {name}
            </Anchor>
        ) : (
            <Text size="sm" fw={500}>
                {name}
            </Text>
        )

    if (model === 'third-party') {
        return (
            <Stack gap={2}>
                {nameElement}
                <Group gap={7} wrap="nowrap">
                    <Text size="xs" c="dimmed">
                        {thirdPartyRentalCompanyName ?? 'Unknown company'}
                    </Text>
                    <TrackingBadge model={model} />
                </Group>
            </Stack>
        )
    }

    return (
        <Group gap={7} wrap="nowrap">
            {nameElement}
            <TrackingBadge model={model} />
        </Group>
    )
}

function describeRentalTerms(row: RentalTermsRow) {
    if (row.billingTermsMixed) {
        return {status: 'mixed' as const, label: 'Mixed'}
    }
    if (row.billingType == null || row.billingRate == null) {
        return {status: 'missing' as const, label: 'Not set'}
    }
    return {
        status: 'set' as const,
        label: `${formatPrice(Math.round(Number(row.billingRate)))} / ${billingUnitAbbreviations[row.billingType] ?? row.billingType.slice(0, 1).toUpperCase()}`,
    }
}

/**
 * Terms are frozen onto the rental stint at deployment, so missing terms are
 * escalated to a warning badge rather than rendered as plain text.
 */
function RentalTermsCell({row}: {row: RentalTermsRow}) {
    const terms = describeRentalTerms(row)
    if (terms.status === 'missing') {
        return (
            <Badge
                variant="light"
                color="orange"
                size="sm"
                leftSection={<IconAlertTriangle size={11} />}
            >
                Not set
            </Badge>
        )
    }
    return (
        <Text size="sm" c={terms.status === 'mixed' ? 'dimmed' : undefined}>
            {terms.label}
        </Text>
    )
}

function GroupHeaderRow({
    dotColor,
    background,
    label,
    labelColor,
    summary,
    children,
}: {
    dotColor: string
    background: string
    label: string
    labelColor: string
    summary: string
    children?: ReactNode
}) {
    return (
        <Table.Tr bg={background}>
            <Table.Td colSpan={COLUMN_COUNT}>
                <Group gap="xs" wrap="nowrap">
                    <Box w={7} h={7} style={{borderRadius: '50%', background: dotColor}} />
                    <Text size="xs" fw={700} tt="uppercase" c={labelColor} lts="0.05em">
                        {label}
                    </Text>
                    <Text size="xs" c="dimmed">
                        {summary}
                    </Text>
                    <Box style={{flex: 1}} />
                    {children}
                </Group>
            </Table.Td>
        </Table.Tr>
    )
}

function JobCell({
    jobId,
    jobName,
    jobCount,
}: {
    jobId?: string | null
    jobName?: string | null
    jobCount?: number
}) {
    if (jobCount != null && jobCount > 1) {
        return <Text size="sm">{jobCount} jobs</Text>
    }
    if (!jobId || !jobName) {
        return (
            <Text size="sm" c="dimmed">
                —
            </Text>
        )
    }
    return (
        <Anchor component={Link} to={`/jobs/${jobId}`} size="sm">
            {jobName}
        </Anchor>
    )
}

function NotConfirmedCell({
    rentalStintIds,
    canConfirm,
}: {
    rentalStintIds: string[]
    canConfirm: boolean
}) {
    return (
        <Group gap="xs" justify="flex-end" wrap="nowrap">
            <Badge variant="light" color="orange" size="sm" title="Went on rent automatically">
                {rentalStintIds.length > 1
                    ? `${rentalStintIds.length} not confirmed`
                    : 'Not confirmed'}
            </Badge>
            {canConfirm ? (
                <ConfirmOutButton
                    rentalStintIds={rentalStintIds}
                    label={rentalStintIds.length > 1 ? 'Confirm all' : 'Confirm out'}
                />
            ) : null}
        </Group>
    )
}

function ScheduleCell({start, end}: {start: string; end?: string}) {
    return <Text size="sm">{end ? `${start} - ${end}` : start}</Text>
}

function StintCell({deployedAt, returnedAt}: {deployedAt: Date; returnedAt?: Date}) {
    return (
        <Text size="sm" title={stintTimesTitle(deployedAt, returnedAt)}>
            {stintDatesLabel(deployedAt, returnedAt)}
        </Text>
    )
}

type StockReturnModalProps = {
    opened: boolean
    onClose: () => void
    row: ProjectDeployedEquipment['stockRows'][number] | null
    returning: boolean
    onConfirm: (
        quantity: number,
        yardId: string,
        returnedOn: Date,
        returnedTime: string,
    ) => Promise<void>
}

function StockReturnModal({opened, onClose, row, returning, onConfirm}: StockReturnModalProps) {
    const [quantity, setQuantity] = useState<number | string>(1)
    const [returnedOn, setReturnedOn] = useState<Date>(businessCalendarDate())
    const [returnedTime, setReturnedTime] = useState(formatBusinessTimestamp(new Date(), 'HH:mm'))
    const [yardId, setYardId] = useState<string | null>(null)
    const [quantityError, setQuantityError] = useState<string | null>(null)
    const [returnedOnError, setReturnedOnError] = useState<string | null>(null)
    const [formError, setFormError] = useState<string | null>(null)

    useEffect(() => {
        if (opened) {
            setQuantity(1)
            setReturnedOn(businessCalendarDate())
            setReturnedTime(formatBusinessTimestamp(new Date(), 'HH:mm'))
            setYardId(null)
            setQuantityError(null)
            setReturnedOnError(null)
            setFormError(null)
        }
    }, [opened])

    function dismiss() {
        onClose()
    }

    async function handleConfirm() {
        if (!row) {
            setFormError('Missing equipment row')
            return
        }

        const parsedQuantity = typeof quantity === 'number' ? quantity : Number(quantity)
        if (!Number.isInteger(parsedQuantity) || parsedQuantity < 1) {
            setQuantityError('Must be at least 1')
            return
        }
        if (parsedQuantity > row.quantity) {
            setQuantityError(`Cannot exceed ${row.quantity}`)
            return
        }

        if (returnedOnError || isFutureReturnDate(returnedOn)) {
            setReturnedOnError('Return date cannot be in the future')
            return
        }

        setQuantityError(null)
        setFormError(null)

        if (!yardId) {
            setFormError('Choose an active return yard')
            return
        }

        try {
            await onConfirm(parsedQuantity, yardId, returnedOn, returnedTime)
            dismiss()
        } catch (error) {
            setFormError(error instanceof Error ? error.message : 'Return failed')
        }
    }

    return (
        <Modal
            opened={opened}
            onClose={dismiss}
            title={row ? `Return ${row.rentalAssetTypeName}` : 'Return stock equipment'}
        >
            <Stack gap="md">
                {formError ? (
                    <Alert color="red" title="Unable to return equipment">
                        {formError}
                    </Alert>
                ) : null}

                <ActiveYardSelector value={yardId} onChange={setYardId} />

                <DateInput
                    label="Returned on"
                    value={returnedOn}
                    onChange={(value) => {
                        if (value) {
                            const selectedDate = toLocalDate(value)
                            if (isFutureReturnDate(selectedDate)) {
                                setReturnedOnError('Return date cannot be in the future')
                                return
                            }
                            setReturnedOn(selectedDate)
                            setReturnedOnError(null)
                        }
                    }}
                    maxDate={businessCalendarDate()}
                    error={returnedOnError}
                />
                <TimeInput
                    label="Return time (optional)"
                    value={returnedTime}
                    onChange={(event) => setReturnedTime(event.currentTarget.value)}
                />

                <NumberInput
                    label="Quantity"
                    value={quantity}
                    onChange={setQuantity}
                    min={1}
                    max={row?.quantity ?? 1}
                    allowDecimal={false}
                    error={quantityError}
                />

                <Group justify="flex-end" gap="sm">
                    <Button variant="default" onClick={dismiss}>
                        Cancel
                    </Button>
                    <Button
                        onClick={() => void handleConfirm()}
                        loading={returning}
                        disabled={!yardId}
                    >
                        Confirm return
                    </Button>
                </Group>
            </Stack>
        </Modal>
    )
}

type BulkReturnModalProps = {
    opened: boolean
    onClose: () => void
    title: string
    count: number
    returning: boolean
    yardId: string | null
    onYardIdChange: (value: string | null) => void
    error: string | null
    onConfirm: (returnedOn: Date, returnedTime: string) => Promise<void>
}

function BulkReturnModal({
    opened,
    onClose,
    title,
    count,
    returning,
    yardId,
    onYardIdChange,
    error,
    onConfirm,
}: BulkReturnModalProps) {
    const [returnedOn, setReturnedOn] = useState<Date>(businessCalendarDate())
    const [returnedTime, setReturnedTime] = useState(formatBusinessTimestamp(new Date(), 'HH:mm'))
    const [returnedOnError, setReturnedOnError] = useState<string | null>(null)

    useEffect(() => {
        if (opened) {
            setReturnedOn(businessCalendarDate())
            setReturnedTime(formatBusinessTimestamp(new Date(), 'HH:mm'))
            setReturnedOnError(null)
        }
    }, [opened])

    async function handleConfirm() {
        if (returnedOnError || isFutureReturnDate(returnedOn)) {
            setReturnedOnError('Return date cannot be in the future')
            return
        }
        await onConfirm(returnedOn, returnedTime)
    }

    return (
        <Modal opened={opened} onClose={onClose} title={title}>
            <Stack gap="md">
                {error ? (
                    <Alert color="red" title="Unable to return equipment">
                        {error}
                    </Alert>
                ) : null}
                <ActiveYardSelector value={yardId} onChange={onYardIdChange} />
                <DateInput
                    label="Returned on"
                    value={returnedOn}
                    onChange={(value) => {
                        if (value) {
                            const selectedDate = toLocalDate(value)
                            if (isFutureReturnDate(selectedDate)) {
                                setReturnedOnError('Return date cannot be in the future')
                                return
                            }
                            setReturnedOn(selectedDate)
                            setReturnedOnError(null)
                        }
                    }}
                    maxDate={businessCalendarDate()}
                    error={returnedOnError}
                />
                <TimeInput
                    label="Return time (optional)"
                    value={returnedTime}
                    onChange={(event) => setReturnedTime(event.currentTarget.value)}
                />
                <Text size="sm">
                    Return {count} selected item{count === 1 ? '' : 's'}?
                </Text>
                <Group justify="flex-end" gap="sm">
                    <Button variant="default" onClick={onClose}>
                        Cancel
                    </Button>
                    <Button
                        onClick={() => void handleConfirm()}
                        loading={returning}
                        disabled={!yardId}
                    >
                        Confirm return
                    </Button>
                </Group>
            </Stack>
        </Modal>
    )
}

export function ProjectDeployedEquipmentSection({
    projectId,
    customerId,
    jobs,
    canConfirmOut,
}: ProjectDeployedEquipmentSectionProps) {
    const {data, isLoading, isError, error} = useProjectDeployedEquipment(projectId)
    const {canDispatch} = useEditRights()
    const invalidateProjectDeployedEquipment = useInvalidateProjectDeployedEquipment()
    const returnStockEquipment = useReturnProjectStockEquipment(projectId)
    const returnIdentifiedEquipment = useReturnProjectIdentifiedEquipment(projectId)
    const returnThirdPartyEquipment = useReturnProjectThirdPartyEquipment(projectId)

    const [stockRowPendingReturn, setStockRowPendingReturn] = useState<
        ProjectDeployedEquipment['stockRows'][number] | null
    >(null)
    const [selectedIdentifiedAssetIds, setSelectedIdentifiedAssetIds] = useState<string[]>([])
    const [selectedStockRowKeys, setSelectedStockRowKeys] = useState<string[]>([])
    const [identifiedConfirmOpen, setIdentifiedConfirmOpen] = useState(false)
    const [identifiedYardId, setIdentifiedYardId] = useState<string | null>(null)
    const [identifiedError, setIdentifiedError] = useState<string | null>(null)
    const [selectedThirdPartyAssetIds, setSelectedThirdPartyAssetIds] = useState<string[]>([])
    const [thirdPartyConfirmOpen, setThirdPartyConfirmOpen] = useState(false)
    const [thirdPartyYardId, setThirdPartyYardId] = useState<string | null>(null)
    const [thirdPartyError, setThirdPartyError] = useState<string | null>(null)
    const [deployPendingRow, setDeployPendingRow] = useState<ProjectPendingEquipmentRow | null>(
        null,
    )
    const [selectedPendingKey, setSelectedPendingKey] = useState<string | null>(null)
    const [selectedReturnedStintIds, setSelectedReturnedStintIds] = useState<string[]>([])
    const [rentalBillingOpened, setRentalBillingOpened] = useState(false)

    const onSiteRows = useMemo<OnSiteRow[]>(() => {
        if (!data) {
            return []
        }
        return [
            ...data.stockRows.map((row) => ({
                kind: 'stock' as const,
                key: `stock-${stockSourceKey(row)}-${row.locationLabel}`,
                ...row,
            })),
            ...data.identifiedRows.map((row) => ({
                kind: 'identified' as const,
                key: row.rentalAssetId,
                ...row,
            })),
            ...data.thirdPartyRows.map((row) => ({
                kind: 'third-party' as const,
                key: row.rentalAssetId,
                ...row,
            })),
        ]
    }, [data])

    const returnedRows = useMemo<ReturnedRow[]>(() => {
        if (!data) {
            return []
        }
        return [
            ...data.returnedStockRows.map((row) => ({
                kind: 'stock' as const,
                key: returnedRowKey(row),
                ...row,
            })),
            ...data.returnedIdentifiedRows.map((row) => ({
                kind: 'identified' as const,
                key: returnedRowKey(row),
                ...row,
            })),
            ...data.returnedThirdPartyRows.map((row) => ({
                kind: 'third-party' as const,
                key: returnedRowKey(row),
                ...row,
            })),
        ]
    }, [data])

    const pendingRows = data?.pendingRows ?? []
    const onSiteUnitCount = onSiteRows.reduce((total, row) => total + rowQuantity(row), 0)
    const returnedUnitCount = returnedRows.reduce((total, row) => total + rowQuantity(row), 0)
    const onSiteLocationCount = new Set(onSiteRows.map((row) => row.locationLabel)).size
    const pendingUnitCount = pendingRows.reduce((total, row) => total + row.quantity, 0)
    const readyUnitCount = pendingRows
        .filter((row) => row.notReadyReason == null)
        .reduce((total, row) => total + row.quantity, 0)
    const missingTermsCount = pendingRows.filter(
        (row) => !row.deployable && row.notReadyReason == null,
    ).length
    const hasAnyEquipment =
        onSiteRows.length > 0 || pendingRows.length > 0 || returnedRows.length > 0

    const selectedCount =
        selectedStockRowKeys.length +
        selectedIdentifiedAssetIds.length +
        selectedThirdPartyAssetIds.length

    function toggleIdentifiedAsset(rentalAssetId: string, checked: boolean) {
        setSelectedStockRowKeys([])
        setSelectedPendingKey(null)
        setSelectedReturnedStintIds([])
        setSelectedIdentifiedAssetIds((current) =>
            checked ? [...current, rentalAssetId] : current.filter((id) => id !== rentalAssetId),
        )
    }

    function toggleThirdPartyAsset(rentalAssetId: string, checked: boolean) {
        setSelectedStockRowKeys([])
        setSelectedIdentifiedAssetIds([])
        setSelectedPendingKey(null)
        setSelectedReturnedStintIds([])
        setSelectedThirdPartyAssetIds((current) =>
            checked ? [...current, rentalAssetId] : current.filter((id) => id !== rentalAssetId),
        )
    }

    function toggleStockRow(key: string, checked: boolean) {
        setSelectedIdentifiedAssetIds([])
        setSelectedThirdPartyAssetIds([])
        setSelectedPendingKey(null)
        setSelectedReturnedStintIds([])
        setSelectedStockRowKeys(checked ? [key] : [])
    }

    function selectPendingRow(key: string) {
        setSelectedStockRowKeys([])
        setSelectedIdentifiedAssetIds([])
        setSelectedThirdPartyAssetIds([])
        setSelectedReturnedStintIds([])
        setSelectedPendingKey(key)
    }

    function toggleReturnedRow(stintIds: string[], checked: boolean) {
        setSelectedStockRowKeys([])
        setSelectedIdentifiedAssetIds([])
        setSelectedThirdPartyAssetIds([])
        setSelectedPendingKey(null)
        const stintIdsToToggle = new Set(stintIds)
        setSelectedReturnedStintIds((current) =>
            checked
                ? [...new Set([...current, ...stintIds])]
                : current.filter((stintId) => !stintIdsToToggle.has(stintId)),
        )
    }

    function openIdentifiedReturn(rentalAssetIds?: string[]) {
        if (rentalAssetIds) {
            setSelectedIdentifiedAssetIds(rentalAssetIds)
        }
        setIdentifiedError(null)
        setIdentifiedYardId(null)
        setIdentifiedConfirmOpen(true)
    }

    function openThirdPartyReturn(rentalAssetIds?: string[]) {
        if (rentalAssetIds) {
            setSelectedThirdPartyAssetIds(rentalAssetIds)
        }
        setThirdPartyError(null)
        setThirdPartyYardId(null)
        setThirdPartyConfirmOpen(true)
    }

    function openSelectedReturn() {
        if (selectedStockRowKeys.length > 0) {
            const stockRow = data?.stockRows.find(
                (row) =>
                    `stock-${stockSourceKey(row)}-${row.locationLabel}` === selectedStockRowKeys[0],
            )
            setStockRowPendingReturn(stockRow ?? null)
        } else if (selectedIdentifiedAssetIds.length > 0) {
            openIdentifiedReturn()
        } else if (selectedThirdPartyAssetIds.length > 0) {
            openThirdPartyReturn()
        }
    }

    function openSelectedDeploy() {
        if (!selectedPendingKey) {
            return
        }
        const row = pendingRows.find(
            (candidate) =>
                `${candidate.jobId}-${stockSourceKey(candidate)}-${candidate.rentalAssetId ?? 'stock'}` ===
                selectedPendingKey,
        )
        setDeployPendingRow(row ?? null)
    }

    async function handleStockReturn(
        quantity: number,
        yardId: string,
        returnedOn: Date,
        returnedTime: string,
    ) {
        const row = stockRowPendingReturn
        if (!row) {
            throw new Error('No on-site stock row selected for return')
        }
        await returnStockEquipment.mutateAsync({
            rentalAssetTypeId: row.rentalAssetTypeId,
            thirdPartyRentalAssetOfferingId: row.thirdPartyRentalAssetOfferingId ?? undefined,
            quantity,
            yardId,
            returnedOn,
            returnedTime,
        })
        setSelectedStockRowKeys([])
    }

    async function handleIdentifiedReturn(returnedOn: Date, returnedTime: string) {
        setIdentifiedError(null)
        try {
            await returnIdentifiedEquipment.mutateAsync({
                rentalAssetIds: selectedIdentifiedAssetIds,
                yardId: identifiedYardId ?? '',
                returnedOn,
                returnedTime,
            })
            setSelectedIdentifiedAssetIds([])
            setIdentifiedYardId(null)
            setIdentifiedConfirmOpen(false)
        } catch (returnError) {
            setIdentifiedError(returnError instanceof Error ? returnError.message : 'Return failed')
        }
    }

    async function handleThirdPartyReturn(returnedOn: Date, returnedTime: string) {
        setThirdPartyError(null)
        try {
            await returnThirdPartyEquipment.mutateAsync({
                rentalAssetIds: selectedThirdPartyAssetIds,
                yardId: thirdPartyYardId ?? '',
                returnedOn,
                returnedTime,
            })
            setSelectedThirdPartyAssetIds([])
            setThirdPartyYardId(null)
            setThirdPartyConfirmOpen(false)
        } catch (returnError) {
            setThirdPartyError(returnError instanceof Error ? returnError.message : 'Return failed')
        }
    }

    if (isLoading) {
        return (
            <Paper withBorder p="md" radius="md">
                <Group justify="center" py="md">
                    <Loader size="sm" />
                </Group>
            </Paper>
        )
    }

    if (isError || !data) {
        return (
            <Paper withBorder p="md" radius="md">
                <Alert color="red" title="Could not load equipment">
                    {error instanceof Error ? error.message : 'Failed to load equipment'}
                </Alert>
            </Paper>
        )
    }

    return (
        <>
            <Paper withBorder p="md" radius="md">
                <Stack gap="md">
                    <Group justify="space-between" align="center" wrap="nowrap">
                        <Group gap="sm" align="center">
                            <Title order={4}>Equipment</Title>
                            <Badge variant="light" color="gray">
                                {onSiteUnitCount}
                            </Badge>
                            {missingTermsCount > 0 ? (
                                <Badge
                                    variant="light"
                                    color="orange"
                                    leftSection={<IconAlertTriangle size={12} />}
                                >
                                    {missingTermsCount} missing terms
                                </Badge>
                            ) : null}
                        </Group>
                    </Group>
                    {!hasAnyEquipment ? (
                        <Stack gap={4} align="center" py="xl">
                            <Text size="sm" fw={600}>
                                No equipment on this order
                            </Text>
                            <Text size="sm" c="dimmed" ta="center" maw={340}>
                                Equipment gets here by being assigned to a job first. Assign it on a
                                job, then deploy it from here or from the driver&rsquo;s job screen.
                            </Text>
                        </Stack>
                    ) : (
                        <Table.ScrollContainer minWidth={900}>
                            <Table verticalSpacing="xs" highlightOnHover>
                                <Table.Thead>
                                    <Table.Tr>
                                        <Table.Th w={44} />
                                        <Table.Th>Equipment</Table.Th>
                                        <Table.Th w={70}>Qty</Table.Th>
                                        <Table.Th>Location / Job</Table.Th>
                                        <Table.Th>Schedule</Table.Th>
                                        <Table.Th>Rental terms</Table.Th>
                                        <Table.Th ta="right">Billing</Table.Th>
                                    </Table.Tr>
                                </Table.Thead>
                                <Table.Tbody>
                                    <GroupHeaderRow
                                        dotColor="var(--mt-signal-attention)"
                                        background="var(--mt-signal-attention-0)"
                                        label="Assigned, not deployed"
                                        labelColor="var(--mt-signal-attention-9)"
                                        summary={
                                            pendingRows.length === 0
                                                ? 'All assigned equipment has been deployed'
                                                : readyUnitCount === pendingUnitCount
                                                  ? `${formatUnits(pendingUnitCount)} · ready to deploy`
                                                  : `${formatUnits(pendingUnitCount)} · ${readyUnitCount} ready to deploy`
                                        }
                                    >
                                        <GroupActionButton
                                            hint="Select assigned rows first"
                                            enabled={selectedPendingKey != null}
                                            onClick={openSelectedDeploy}
                                        >
                                            Deploy
                                        </GroupActionButton>
                                    </GroupHeaderRow>
                                    {pendingRows.map((row) => {
                                        const key = `${row.jobId}-${stockSourceKey(row)}-${row.rentalAssetId ?? 'stock'}`
                                        return (
                                            <Table.Tr key={key}>
                                                <Table.Td>
                                                    <Radio
                                                        size="xs"
                                                        name="pending-equipment"
                                                        value={key}
                                                        checked={selectedPendingKey === key}
                                                        disabled={
                                                            !canDispatch ||
                                                            row.notReadyReason != null
                                                        }
                                                        onChange={() => selectPendingRow(key)}
                                                        aria-label={`Select ${pendingEquipmentName(row)}`}
                                                    />
                                                </Table.Td>
                                                <Table.Td>
                                                    <EquipmentCell
                                                        name={pendingEquipmentName(row)}
                                                        model={pendingTrackingModel(row)}
                                                        link={pendingEquipmentNameLink(row)}
                                                        thirdPartyRentalCompanyName={
                                                            row.thirdPartyRentalCompanyName
                                                        }
                                                    />
                                                </Table.Td>
                                                <Table.Td>
                                                    <Text size="sm" fw={700}>
                                                        {row.quantity}
                                                    </Text>
                                                </Table.Td>
                                                <Table.Td>
                                                    <JobCell
                                                        jobId={row.jobId}
                                                        jobName={row.jobName}
                                                    />
                                                </Table.Td>
                                                <Table.Td>
                                                    <ScheduleCell
                                                        start={format(
                                                            row.jobScheduledDate,
                                                            'M/d/yy',
                                                        )}
                                                    />
                                                </Table.Td>
                                                <Table.Td>
                                                    <RentalTermsCell row={row} />
                                                </Table.Td>
                                                <Table.Td ta="right">
                                                    {row.notReadyReason ? (
                                                        <Badge
                                                            variant="light"
                                                            color="gray"
                                                            size="sm"
                                                            title="Not ready to deploy"
                                                        >
                                                            {row.notReadyReason}
                                                        </Badge>
                                                    ) : (
                                                        <Text size="sm" c="dimmed">
                                                            —
                                                        </Text>
                                                    )}
                                                </Table.Td>
                                            </Table.Tr>
                                        )
                                    })}

                                    <GroupHeaderRow
                                        dotColor="var(--mt-signal-positive)"
                                        background="var(--mt-signal-positive-0)"
                                        label="On site"
                                        labelColor="var(--mt-signal-positive-9)"
                                        summary={`${formatUnits(onSiteUnitCount)} · ${onSiteLocationCount} location${onSiteLocationCount === 1 ? '' : 's'}`}
                                    >
                                        <GroupActionButton
                                            hint="Select on-site rows first"
                                            enabled={selectedCount > 0}
                                            onClick={openSelectedReturn}
                                        >
                                            Return
                                            {selectedCount > 0 ? ` · ${selectedCount}` : ''}
                                        </GroupActionButton>
                                    </GroupHeaderRow>
                                    {onSiteRows.map((row) => {
                                        const name = equipmentName(row)
                                        const key = row.key
                                        const checked =
                                            row.kind === 'stock'
                                                ? selectedStockRowKeys.includes(key)
                                                : row.kind === 'identified'
                                                  ? selectedIdentifiedAssetIds.includes(
                                                        row.rentalAssetId,
                                                    )
                                                  : selectedThirdPartyAssetIds.includes(
                                                        row.rentalAssetId,
                                                    )
                                        return (
                                            <Table.Tr key={key}>
                                                <Table.Td>
                                                    <Checkbox
                                                        size="xs"
                                                        checked={checked}
                                                        disabled={!canDispatch}
                                                        onChange={(event) =>
                                                            row.kind === 'stock'
                                                                ? toggleStockRow(
                                                                      key,
                                                                      event.currentTarget.checked,
                                                                  )
                                                                : row.kind === 'identified'
                                                                  ? toggleIdentifiedAsset(
                                                                        row.rentalAssetId,
                                                                        event.currentTarget.checked,
                                                                    )
                                                                  : toggleThirdPartyAsset(
                                                                        row.rentalAssetId,
                                                                        event.currentTarget.checked,
                                                                    )
                                                        }
                                                        aria-label={`Select ${name}`}
                                                    />
                                                </Table.Td>
                                                <Table.Td>
                                                    <EquipmentCell
                                                        name={name}
                                                        model={
                                                            row.kind === 'stock' &&
                                                            row.party === 'third_party'
                                                                ? 'third-party'
                                                                : row.kind
                                                        }
                                                        link={equipmentNameLink(row)}
                                                        thirdPartyRentalCompanyName={
                                                            row.kind === 'third-party' ||
                                                            row.kind === 'stock'
                                                                ? row.thirdPartyRentalCompanyName
                                                                : undefined
                                                        }
                                                    />
                                                </Table.Td>
                                                <Table.Td>
                                                    <Text
                                                        size="sm"
                                                        fw={row.kind === 'stock' ? 700 : 400}
                                                    >
                                                        {rowQuantity(row)}
                                                    </Text>
                                                </Table.Td>
                                                <Table.Td>
                                                    <Stack gap={0}>
                                                        <Text size="sm">{row.locationLabel}</Text>
                                                        <JobCell
                                                            jobId={row.jobId}
                                                            jobName={row.jobName}
                                                            jobCount={
                                                                row.kind === 'stock'
                                                                    ? (row.jobCount ?? undefined)
                                                                    : undefined
                                                            }
                                                        />
                                                    </Stack>
                                                </Table.Td>
                                                <Table.Td>
                                                    <StintCell deployedAt={row.deployedAt} />
                                                </Table.Td>
                                                <Table.Td>
                                                    <RentalTermsCell row={row} />
                                                </Table.Td>
                                                <Table.Td ta="right">
                                                    {row.unconfirmedRentalStintIds.length > 0 ? (
                                                        <NotConfirmedCell
                                                            rentalStintIds={
                                                                row.unconfirmedRentalStintIds
                                                            }
                                                            canConfirm={canConfirmOut}
                                                        />
                                                    ) : (
                                                        <Text size="sm" c="dimmed">
                                                            —
                                                        </Text>
                                                    )}
                                                </Table.Td>
                                            </Table.Tr>
                                        )
                                    })}

                                    <GroupHeaderRow
                                        dotColor="var(--mt-signal-neutral)"
                                        background="var(--mt-surface-subtle)"
                                        label="Returned"
                                        labelColor="var(--mt-text-secondary)"
                                        summary={`${formatUnits(returnedUnitCount)} · Unbilled ${formatPrice(returnedRows.reduce((total, row) => total + (row.remainingAmount ?? 0), 0))}${returnedRows.filter((row) => row.billingState === 'not_billable' || row.billingState === 'no_rental_record').length > 0 ? ` · ${returnedRows.filter((row) => row.billingState === 'not_billable' || row.billingState === 'no_rental_record').length} excluded` : ''}`}
                                    >
                                        <GroupActionButton
                                            hint="Select unbilled returned rows first"
                                            enabled={selectedReturnedStintIds.length > 0}
                                            onClick={() => setRentalBillingOpened(true)}
                                        >
                                            Add rental charges
                                            {selectedReturnedStintIds.length > 0
                                                ? ` · ${selectedReturnedStintIds.length}`
                                                : ''}
                                        </GroupActionButton>
                                    </GroupHeaderRow>
                                    {returnedRows.length === 0 ? (
                                        <Table.Tr>
                                            <Table.Td colSpan={COLUMN_COUNT}>
                                                <Text size="sm" c="dimmed">
                                                    No returned equipment yet.
                                                </Text>
                                            </Table.Td>
                                        </Table.Tr>
                                    ) : (
                                        returnedRows.map((row) => {
                                            const name = equipmentName(row)
                                            const selectable =
                                                row.billingState === 'unbilled' &&
                                                row.rentalStintIds.length > 0
                                            const checked =
                                                row.rentalStintIds.length > 0 &&
                                                row.rentalStintIds.every((id) =>
                                                    selectedReturnedStintIds.includes(id),
                                                )
                                            const billingLabel =
                                                row.billingState === 'unbilled'
                                                    ? formatPrice(row.remainingAmount ?? 0)
                                                    : row.billingState === 'billed'
                                                      ? `${formatPrice(row.chargedAmount ?? 0)}${row.invoiceCount > 1 ? ` · ${row.invoiceCount} invoices` : ''}`
                                                      : row.billingState === 'not_billable'
                                                        ? row.billingType === 'hourly'
                                                            ? 'Hourly'
                                                            : 'No rental terms'
                                                        : 'No rental record'
                                            const selectionDisabledReason =
                                                row.billingState === 'billed'
                                                    ? 'This charge has already been created.'
                                                    : undefined
                                            return (
                                                <Table.Tr key={row.key}>
                                                    <Table.Td>
                                                        <Tooltip
                                                            label={selectionDisabledReason}
                                                            disabled={
                                                                selectionDisabledReason == null
                                                            }
                                                        >
                                                            <span>
                                                                <Checkbox
                                                                    size="xs"
                                                                    checked={checked}
                                                                    disabled={
                                                                        !selectable || !canDispatch
                                                                    }
                                                                    onChange={(event) =>
                                                                        toggleReturnedRow(
                                                                            row.rentalStintIds,
                                                                            event.currentTarget
                                                                                .checked,
                                                                        )
                                                                    }
                                                                    aria-label={returnedSelectionLabel(
                                                                        name,
                                                                        row.returnedAt,
                                                                    )}
                                                                />
                                                            </span>
                                                        </Tooltip>
                                                    </Table.Td>
                                                    <Table.Td>
                                                        <EquipmentCell
                                                            name={name}
                                                            model={
                                                                row.kind === 'stock' &&
                                                                row.party === 'third_party'
                                                                    ? 'third-party'
                                                                    : row.kind
                                                            }
                                                            link={equipmentNameLink(row)}
                                                            thirdPartyRentalCompanyName={
                                                                row.kind === 'third-party' ||
                                                                row.kind === 'stock'
                                                                    ? row.thirdPartyRentalCompanyName
                                                                    : undefined
                                                            }
                                                        />
                                                    </Table.Td>
                                                    <Table.Td>
                                                        <Text
                                                            size="sm"
                                                            fw={row.kind === 'stock' ? 700 : 400}
                                                        >
                                                            {rowQuantity(row)}
                                                        </Text>
                                                    </Table.Td>
                                                    <Table.Td>
                                                        <Stack gap={0}>
                                                            <Text size="sm">
                                                                {row.locationLabel}
                                                            </Text>
                                                            <JobCell
                                                                jobId={row.jobId}
                                                                jobName={row.jobName}
                                                                jobCount={
                                                                    row.kind === 'stock'
                                                                        ? (row.jobCount ??
                                                                          undefined)
                                                                        : undefined
                                                                }
                                                            />
                                                        </Stack>
                                                    </Table.Td>
                                                    <Table.Td>
                                                        <StintCell
                                                            deployedAt={row.deployedAt}
                                                            returnedAt={row.returnedAt}
                                                        />
                                                    </Table.Td>
                                                    <Table.Td>
                                                        <RentalTermsCell row={row} />
                                                    </Table.Td>
                                                    <Table.Td ta="right">
                                                        <Text size="sm" fw={600}>
                                                            {billingLabel}
                                                        </Text>
                                                        {row.invoiceId ? (
                                                            <Anchor
                                                                component={Link}
                                                                to={`/invoices/${row.invoiceId}`}
                                                                size="xs"
                                                            >
                                                                Invoice
                                                            </Anchor>
                                                        ) : null}
                                                    </Table.Td>
                                                </Table.Tr>
                                            )
                                        })
                                    )}
                                </Table.Tbody>
                            </Table>
                        </Table.ScrollContainer>
                    )}
                </Stack>
            </Paper>

            <DeployEquipmentDialog
                opened={deployPendingRow != null}
                onClose={() => setDeployPendingRow(null)}
                onDeployed={() => setSelectedPendingKey(null)}
                projectId={projectId}
                customerId={customerId}
                jobs={jobs}
                pendingRow={deployPendingRow}
            />

            <StockReturnModal
                opened={stockRowPendingReturn != null}
                onClose={() => setStockRowPendingReturn(null)}
                row={stockRowPendingReturn}
                returning={returnStockEquipment.isPending}
                onConfirm={handleStockReturn}
            />

            <BulkReturnModal
                opened={identifiedConfirmOpen}
                onClose={() => setIdentifiedConfirmOpen(false)}
                title="Return identified equipment"
                count={selectedIdentifiedAssetIds.length}
                returning={returnIdentifiedEquipment.isPending}
                yardId={identifiedYardId}
                onYardIdChange={setIdentifiedYardId}
                error={identifiedError}
                onConfirm={handleIdentifiedReturn}
            />

            <BulkReturnModal
                opened={thirdPartyConfirmOpen}
                onClose={() => setThirdPartyConfirmOpen(false)}
                title="Return third-party equipment"
                count={selectedThirdPartyAssetIds.length}
                returning={returnThirdPartyEquipment.isPending}
                yardId={thirdPartyYardId}
                onYardIdChange={setThirdPartyYardId}
                error={thirdPartyError}
                onConfirm={handleThirdPartyReturn}
            />
            <RentalBillingModal
                projectId={projectId}
                opened={rentalBillingOpened}
                projectScoped
                rentalStintIds={
                    selectedReturnedStintIds.length > 0 ? selectedReturnedStintIds : undefined
                }
                onClose={() => setRentalBillingOpened(false)}
                onChargesCreated={async () => {
                    invalidateProjectDeployedEquipment(projectId)
                    setSelectedReturnedStintIds([])
                }}
            />
        </>
    )
}
